import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Subscription, firstValueFrom } from 'rxjs';
import * as showdown from 'showdown';

import { ChatFireService } from '../../gen2ai/chatfire.service';
import { Conversation, Message } from '../../models/chat.model';
import { showdownMarkdownClassesExtension } from '../../shared/markdown/showdown-md-classes.extension';

interface ConversationStats {
  startTime?: Date | null;
  endTime?: Date | null;
  messageCount: number;
  totalTokens: number;
  roleCounts: Record<string, number>;
  models: string[];
}

interface LetterStats {
  vowels: number;
  consonants: number;
  totalLetters: number;
}

@Component({
  selector: 'app-search-detail',
  templateUrl: './search-detail.component.html',
  styleUrls: ['./search-detail.component.scss']
})
export class SearchDetailComponent implements OnInit, OnDestroy {
  conversationId = '';
  conversation: Conversation | null = null;
  messages: Message[] = [];
  stats: ConversationStats = {
    startTime: null,
    endTime: null,
    messageCount: 0,
    totalTokens: 0,
    roleCounts: {},
    models: []
  };
  letterStats: LetterStats = {
    vowels: 0,
    consonants: 0,
    totalLetters: 0
  };
  loading = true;
  error: string | null = null;

  private converter = new showdown.Converter({
    tables: true,
    strikethrough: true,
    tasklists: true,
    ghCompatibleHeaderId: true,
    extensions: [showdownMarkdownClassesExtension({ textClass: 'md-text' })]
  });
  private subs = new Subscription();

  constructor(
    private route: ActivatedRoute,
    private chatFire: ChatFireService
  ) {}

  ngOnInit(): void {
    this.subs.add(
      this.route.paramMap.subscribe((params) => {
        const id = params.get('id') || '';
        if (!id) {
          this.error = 'No conversation id provided.';
          this.loading = false;
          return;
        }
        this.conversationId = id;
        this.fetchConversation(id);
      })
    );
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  convertMarkdown(markdown: string): string {
    return this.converter.makeHtml(markdown || '');
  }

  private async fetchConversation(id: string): Promise<void> {
    this.loading = true;
    this.error = null;
    try {
      const conversation = await firstValueFrom(this.chatFire.loadConversationById(id));
      if (!conversation) {
        this.error = 'Conversation not found.';
        this.conversation = null;
        this.messages = [];
        this.stats = {
          startTime: null,
          endTime: null,
          messageCount: 0,
          totalTokens: 0,
          roleCounts: {},
          models: []
        };
        this.letterStats = {
          vowels: 0,
          consonants: 0,
          totalLetters: 0
        };
        this.loading = false;
        return;
      }
      this.conversation = conversation;
      this.messages = [...(conversation.messages || [])].sort((a, b) => {
        const aTime = new Date(a.timestamp).getTime();
        const bTime = new Date(b.timestamp).getTime();
        return aTime - bTime;
      });
      this.stats = this.buildStats(conversation, this.messages);
      this.letterStats = this.countLetters(this.messages.filter((m) => m.role === 'user'));
    } catch {
      this.error = 'Unable to load conversation details.';
    } finally {
      this.loading = false;
    }
  }

  private buildStats(conversation: Conversation, messages: Message[]): ConversationStats {
    const roleCounts: Record<string, number> = {};
    const models = new Set<string>();
    let totalTokens = 0;
    let startTime: Date | null = null;
    let endTime: Date | null = null;

    messages.forEach((msg) => {
      const stamp = new Date(msg.timestamp);
      if (!startTime || stamp < startTime) startTime = stamp;
      if (!endTime || stamp > endTime) endTime = stamp;

      roleCounts[msg.role] = (roleCounts[msg.role] || 0) + 1;

      const modelLabel = msg.modelDisplayName || msg.modelId;
      if (modelLabel) models.add(modelLabel);

      totalTokens += msg.tokens || 0;
    });

    const fallbackStart = conversation.createdAt ? new Date(conversation.createdAt) : null;
    const fallbackEnd = conversation.updatedAt ? new Date(conversation.updatedAt) : null;

    return {
      startTime: startTime || fallbackStart,
      endTime: endTime || fallbackEnd,
      messageCount: messages.length,
      totalTokens,
      roleCounts,
      models: Array.from(models.values())
    };
  }

  private countLetters(messages: Message[]): LetterStats {
    const joined = messages.map((m) => m.content || '').join(' ');
    const letters = joined.match(/[a-z]/gi) || [];
    const vowels = letters.filter((char) => /[aeiou]/i.test(char)).length;
    const consonants = letters.length - vowels;

    return {
      vowels,
      consonants,
      totalLetters: letters.length
    };
  }

  truncateConversationTopic(topic: string): string {
    const value = String(topic || '').trim();
    if (!value) return 'Conversation';
    return value.length <= 13 ? value : `${value.slice(0, 13)}...`;
  }
}
